import React from 'react';
import { components } from 'react-select';
import { AsyncPaginate, wrapMenuList } from 'react-select-async-paginate';

import styles from './styles.module.css';

interface InfiniteSelectProps {
  fetcherFn: (
    search: string,
    page: number,
  ) => Promise<{ results: any[]; hasMore: boolean }>;
  placeholder: string;
  onChange: (selectedOptions: any[]) => void;
  value: any[];
  filterKey?: string;
  cacheKey?: string;
  isDisabled?: boolean;
  defaultOptions?: any[];
  selectedLabel?: string;
  optionsLabel?: string;
  valueContainerLabel?: string;
  getOptionLabel?: (option: any) => string;
  getOptionValue?: (option: any) => string;
}

const InfiniteSelect: React.FC<InfiniteSelectProps> = ({
  filterKey,
  cacheKey,
  fetcherFn,
  placeholder,
  onChange,
  value,
  isDisabled = false,
  defaultOptions = [],
  selectedLabel = 'Selected',
  optionsLabel = 'Options',
  valueContainerLabel = 'Multiple',
  getOptionLabel = (option) => option.label,
  getOptionValue = (option) => option.value,
}) => {
  const loadOptions = async (search, loadedOptions, pageParam) => {
    // console.log("param1: ", search);
    // console.log("param2: ", loadedOptions);
    // console.log("param3: ", pageParam);
    const { page } = pageParam;
    const { results, hasMore } = await fetcherFn(search, page);
    const key = filterKey ?? 'id';

    // create a Set of already loaded keys based on filterKey
    const existingKeys = new Set(loadedOptions.map((opt) => opt[key]));

    // Filter new results to exclude any with existing keys
    const deduplicatedResults = (results || []).filter(
      (item) => !existingKeys.has(item[key]),
    );

    return {
      options: deduplicatedResults,
      hasMore,
      additional: {
        page: page + 1,
      },
    };
  };

  const handleChange = (selectedOptions) => {
    onChange(selectedOptions);
  };

  const customValueContainer = ({ children, ...props }: any) => {
    const selectedCount = props.getValue().length;
    const Input = React.Children.toArray(children).find(
      (child: any) => child.type.name === 'Input',
    );

    const style = {
      marginLeft: 'auto',
      background: '#0e74bc',
      color: 'white',
      borderRadius: '4px',
      fontFamily: 'Open Sans',
      fontSize: '11px',
      padding: '3px 7px',
      order: 99,
    };

    return (
      <components.ValueContainer {...props}>
        {selectedCount > 1 ? (
          <div className="d-inline-flex cursor-pointer align-items-center">
            {Input}
            <div
              className="d-flex align-items-center"
              style={{ gap: '5px', color: 'black' }}
            >
              <span>{valueContainerLabel}</span>
              <div style={style}>{selectedCount}</div>
            </div>
          </div>
        ) : selectedCount === 1 ? (
          <div
            className="d-inline-flex align-items-center"
            style={{ gap: '5px', color: 'black' }}
          >
            {Input}
            {getOptionLabel(props.getValue()[0])}
          </div>
        ) : (
          children
        )}
      </components.ValueContainer>
    );
  };

  const CustomMenuList = (props) => {
    const { getValue, setValue, isLoading, options } = props;
    const selectedHosts = getValue();

    const handleHostClick = (host) => {
      const newSelectedHosts = selectedHosts.filter(
        (selected) => getOptionValue(selected) !== getOptionValue(host),
      );
      setValue(newSelectedHosts, 'select-option');
    };

    return (
      <components.MenuList {...props}>
        {selectedHosts.length > 0 && (
          <div>
            <div className={styles.customMenuListHeader}>{selectedLabel}</div>
            {selectedHosts.map((host) => (
              <div
                key={getOptionValue(host)}
                className={styles.customMenuListOption}
                onClick={() => handleHostClick(host)}
              >
                {getOptionLabel(host)}
              </div>
            ))}
          </div>
        )}
        <div className={styles.customMenuListHeader}>{optionsLabel}</div>
        {props.children}
        {isLoading && options.length > 0 && (
          <div className={styles.loadingIndicator}>Loading...</div>
        )}
      </components.MenuList>
    );
  };

  return (
    <AsyncPaginate
      key={cacheKey}
      isMulti
      backspaceRemovesValue={false}
      loadOptions={loadOptions}
      getOptionLabel={getOptionLabel}
      getOptionValue={getOptionValue}
      placeholder={placeholder}
      isDisabled={isDisabled}
      value={value}
      options={defaultOptions}
      defaultOptions={true}
      onChange={handleChange}
      debounceTimeout={500}
      closeMenuOnSelect={false}
      additional={{
        page: 0,
      }}
      styles={{
        control: (base, _) => ({
          ...base,
          cursor: 'pointer',
        }),
        option: (base, state) => ({
          ...base,
          cursor: 'pointer',
          backgroundColor: state.isSelected ? '#0e74bc' : base.backgroundColor,
          color: state.isSelected ? 'white' : 'black',
          ':hover': {
            backgroundColor: state.isSelected ? '#0e74bc' : '#f0f0f0',
            color: state.isSelected ? 'white' : 'black',
          },
        }),
      }}
      components={{
        ValueContainer: customValueContainer,
        MenuList: wrapMenuList(CustomMenuList),
      }}
    />
  );
};

export default InfiniteSelect;
